import { useFieldArray, useForm, useWatch } from 'react-hook-form';

import Modal from '../../../Components/Modal/Modal';
import TimeOfDayInput from '../../../Components/TimeOfDayInput/TimeOfDayInput';

import styles from './CalendarPage.module.css';
import API from '../../../Utils/api';
import { IoCloseSharp } from 'react-icons/io5';
import { CiCircleQuestion } from 'react-icons/ci';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useUser } from '../../../Context/useUser';

// Convert total seconds (float) to "MM:SS.MS" string (MM:SS:milliseconds)
function secondsToRaceTime(totalSeconds) {
    if (!totalSeconds && totalSeconds !== 0) return '';
    const roundedSeconds = Math.round(totalSeconds * 1000) / 1000;
    const mins = Math.floor(roundedSeconds / 60);
    const secs = Math.floor(roundedSeconds % 60);
    const ms = Math.floor((roundedSeconds % 1) * 1000);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
}

// Parse "MM:SS.MS" (MM:SS:milliseconds) string back to total seconds (float)
function raceTimeToSeconds(value) {
    if (!value) return null;
    const str = String(value).trim();

    // Handle MM:SS.MS format
    const colonParts = str.split(':');
    if (colonParts.length === 2) {
        const minutes = parseFloat(colonParts[0]) || 0;
        const secondsPart = colonParts[1];

        // Split seconds and milliseconds
        const dotParts = secondsPart.split('.');
        const seconds = parseFloat(dotParts[0]) || 0;
        const milliseconds = dotParts[1] ? parseFloat('0.' + dotParts[1]) : 0;

        return minutes * 60 + seconds + milliseconds;
    }

    // Handle SS.MS format (no minutes)
    const dotParts = str.split('.');
    const seconds = parseFloat(dotParts[0]) || 0;
    const milliseconds = dotParts[1] ? parseFloat('0.' + dotParts[1]) : 0;
    return seconds + milliseconds;
}

function getExpectedSplitsCount (raceType) {
    switch (raceType) {
        case '50':
            return 2;
        case '100':
            return 2;
        case '200':
            return 4;
        case '400':
            return 8;
        case '800':
            return 16;
        case '1500':
            return 30;
        default:
            return 0;
    }
}

export default function UpdateRace ({
    data: defaultValues,
    closeCB,
    onSuccess,
    onFailure,
    hideDeleteButton
}) {
    const { user } = useUser();

    // Extract time from raceStart if it exists
    const getTimeFromDate = (date) => {
        if (!date) return '09:00';
        const d = new Date(date);
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    const { register, handleSubmit, control, setValue } = useForm({
        defaultValues: {
            ...defaultValues,
            duration: secondsToRaceTime(defaultValues.duration),
            raceStartTime: getTimeFromDate(defaultValues.raceStart),
            raceEndTime: getTimeFromDate(defaultValues.raceEnd)
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'splits'
    });

    const isCompleted =
        user.type !== 'Admin' &&
        (defaultValues.status === 'Completed' || defaultValues.status === 'Declined');

    const onEventUpdate = async newData => {
        if (isCompleted && newData.status === 'Assigned') {
            onFailure('Cannot change a completed or declined race back to assigned!');
            return;
        }

        // Validate for commas in decimal inputs
        if (newData.duration && typeof newData.duration === 'string' && newData.duration.includes(',')) {
            onFailure('Please use a period (.) instead of a comma (,) for decimal numbers in Official Time!');
            return;
        }

        // Validate splits for commas
        if (newData.splits && Array.isArray(newData.splits)) {
            for (let i = 0; i < newData.splits.length; i++) {
                const split = newData.splits[i];
                if (split.duration && typeof split.duration === 'string' && split.duration.includes(',')) {
                    onFailure(`Please use a period (.) instead of a comma (,) for decimal numbers in Split ${i + 1} Duration!`);
                    return;
                }
                if (split.volume && typeof split.volume === 'string' && split.volume.includes(',')) {
                    onFailure(`Please use a period (.) instead of a comma (,) for decimal numbers in Split ${i + 1} Volume!`);
                    return;
                }
                if (split.T && typeof split.T === 'string' && split.T.includes(',')) {
                    onFailure(`Please use a period (.) instead of a comma (,) for decimal numbers in Split ${i + 1} T!`);
                    return;
                }
            }
        }

        try {
            // Handle raceStart and raceEnd times BEFORE converting raceDate
            if (newData.raceStartTime) {
                const raceStart = new Date(`${newData.raceDate}T${newData.raceStartTime}`);
                newData.raceStart = raceStart.toISOString();
            }
            if (newData.raceEndTime) {
                const raceEnd = new Date(`${newData.raceDate}T${newData.raceEndTime}`);
                newData.raceEnd = raceEnd.toISOString();
            }

            // Clean up time fields (don't send them to API)
            delete newData.raceStartTime;
            delete newData.raceEndTime;

            // Now convert raceDate
            newData.raceDate = new Date(newData.raceDate).toUTCString();

            const parsedDuration = raceTimeToSeconds(newData.duration);
            if (parsedDuration) {
                newData.duration = parsedDuration;
            } else {
                const sum = (newData.splits || []).reduce((acc, s) => acc + (parseFloat(s?.duration) || 0), 0);
                newData.duration = sum > 0 ? sum : undefined;
            }
            const { data } = await API.updateRace(defaultValues._id, newData);
            onSuccess('Race updated!', data);
        } catch (err) {
            onFailure("Couldn't update race!");
        }
    };

    const onEventRemove = async race => {
        if (isCompleted) {
            onFailure('Cannot delete a completed or declined race!');
            return;
        }
        try {
            await API.deleteRaceById(race._id);
            onSuccess('Race removed!', defaultValues);
        } catch (err) {
            onFailure("Couldn't remove race!");
        }
    };

    const onAddSplit = e => {
        append({ volume: 50, duration: 0, T: 0 });
    };

    const onRemoveSplit = id => {
        const splitToRemove = fields.findIndex(split => split.id === id);
        remove(splitToRemove);
    };

    const raceType = useWatch({ control, name: 'raceType' });
    const expectedSplits = getExpectedSplitsCount(raceType);

    const splits = useWatch({ control, name: 'splits' });
    const splitsSum = (splits || []).reduce((sum, s) => sum + (parseFloat(s?.duration) || 0), 0);

    const onDurationFocus = e => {
        if (!e.target.value && splitsSum > 0) {
            setValue('duration', secondsToRaceTime(splitsSum));
        }
    };

    return (
        <Modal
            closeCB={closeCB}
            title={'Update Race'}
            footer={
                <div className={styles.row}>
                    {!hideDeleteButton && (
                        <button
                            className='btn btn-danger'
                            onClick={() => onEventRemove(defaultValues)}
                        >
                            Remove
                        </button>
                    )}
                    {!isCompleted && (
                        <button className='btn btn-primary' onClick={handleSubmit(onEventUpdate)}>
                            Update
                        </button>
                    )}
                </div>
            }
        >
            <form className={styles.form}>
                <div className='form-group'>
                    <label htmlFor='status'>Status</label>
                    <select
                        {...register('status', { required: true })}
                        className='form-control'
                        disabled={isCompleted}
                    >
                        <option value='Assigned'>Assigned</option>
                        <option value='Completed'>Completed</option>
                        <option value='Declined'>Declined</option>
                    </select>
                </div>
                <div className='form-group'>
                    <label htmlFor='raceDate'>Race Date</label>
                    <input
                        type='date'
                        {...register('raceDate', { required: true })}
                        className='form-control'
                        disabled={isCompleted}
                        lang='en'
                    />
                </div>
                <div className={`${styles.row} form-row`} style={{ gap: '1rem' }}>
                    <div className='form-group' style={{ flex: 1 }}>
                        <TimeOfDayInput
                            id='raceStartTime'
                            label='Start Time'
                            {...register('raceStartTime', { required: true })}
                            disabled={isCompleted}
                        />
                    </div>
                    <div className='form-group' style={{ flex: 1 }}>
                        <TimeOfDayInput
                            id='raceEndTime'
                            label='End Time'
                            {...register('raceEndTime', { required: true })}
                            disabled={isCompleted}
                        />
                    </div>
                </div>
                <div className={`${styles.row} form-row`} style={{ gap: '1rem' }}>
                    <div className='form-group' style={{ flex: 1 }}>
                        <label htmlFor='raceStyle'>Race Style</label>
                        <select
                            className='form-control'
                            {...register('raceStyle', { required: true })}
                            disabled={isCompleted}
                        >
                            <option value={'Freestyle'}>Freestyle</option>
                            <option value={'Backstroke'}>Backstroke</option>
                            <option value={'Breaststroke'}>Breaststroke</option>
                            <option value={'Butterfly'}>Butterfly</option>
                            <option value={'IM'}>Individual Medley</option>
                        </select>
                    </div>
                    <div className='form-group' style={{ flex: 1 }}>
                        <label htmlFor='raceType'>Race Distance</label>
                        <select
                            className='form-control'
                            {...register('raceType', { required: true })}
                            disabled={isCompleted}
                        >
                            <option value={'50'}>50</option>
                            <option value={'100'}>100</option>
                            <option value={'200'}>200</option>
                            <option value={'400'}>400</option>
                            <option value={'800'}>800</option>
                            <option value={'1500'}>1500</option>
                        </select>
                    </div>
                </div>
                <div className={`${styles.row} form-row`} style={{ gap: '1rem' }}>
                    <div className='form-group' style={{ flex: 1 }}>
                        <label htmlFor='duration'>
                            Official Time
                            {splitsSum > 0 && (
                                <span style={{ fontWeight: 'normal', color: 'var(--color-text-secondary)', marginLeft: 8 }}>
                                    — splits sum: {secondsToRaceTime(splitsSum)}
                                </span>
                            )}
                        </label>
                        <input
                            type='text'
                            {...register('duration')}
                            className='form-control'
                            disabled={isCompleted}
                            placeholder={splitsSum > 0 ? secondsToRaceTime(splitsSum) : 'MM:SS.ms (e.g. 1:54.32)'}
                            onFocus={onDurationFocus}
                        />
                    </div>
                    <div className='form-group' style={{ flex: 1 }}>
                        <label htmlFor='raceLocation'>Location</label>
                        <input
                            type='text'
                            {...register('raceLocation', { required: true })}
                            className='form-control'
                            disabled={isCompleted}
                        />
                    </div>
                </div>
                <div className='form-group'>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                        <label htmlFor='splits'>Splits</label>
                        <OverlayTrigger
                            placement='right'
                            overlay={
                                <Tooltip id='splits-tooltip'>
                                    {expectedSplits > 0 ? (
                                        <>
                                            <div>
                                                <strong>
                                                    You need to provide {expectedSplits} split
                                                    {expectedSplits > 1 ? 's' : ''} for this
                                                    distance.
                                                </strong>
                                            </div>
                                            <div style={{ marginTop: 'var(--space-sm)' }}>
                                                <em>Example:</em>
                                                <br />
                                                <span
                                                    style={{ fontSize: '0.97em', color: 'var(--color-accent)' }}
                                                >
                                                    For a 200m race, enter 4 splits (e.g. 50m each).
                                                    <br />
                                                    Each split should have a <b>Volume</b>{' '}
                                                    (distance), <b>Duration</b> (time), and <b>T</b>{' '}
                                                    (optional metric).
                                                </span>
                                            </div>
                                        </>
                                    ) : (
                                        'Select a race distance to see required splits.'
                                    )}
                                </Tooltip>
                            }
                        >
                            <span
                                tabIndex={0}
                                style={{ display: 'inline-flex', alignItems: 'center' }}
                            >
                                <CiCircleQuestion size={20} style={{ cursor: 'pointer', color: 'var(--color-text-secondary)' }} />
                            </span>
                        </OverlayTrigger>
                    </div>
                    {fields.map((field, index) => (
                        <div key={field.id} className={`${styles.row} form-row`}>
                            <IoCloseSharp
                                className={styles.removeButton}
                                onClick={() => onRemoveSplit(field.id)}
                                style={{
                                    display: isCompleted ? 'none' : undefined,
                                    color: 'var(--color-text-secondary)'
                                }}
                            />
                            <div className='form-group'>
                                <label htmlFor='volume'>Volume</label>
                                <input
                                    type='text'
                                    {...register(`splits.${index}.volume`)}
                                    className='form-control'
                                    disabled={isCompleted}
                                />
                            </div>
                            <div className='form-group'>
                                <label htmlFor='duration'>Duration (seconds)</label>
                                <input
                                    type='text'
                                    {...register(`splits.${index}.duration`)}
                                    className='form-control'
                                    disabled={isCompleted}
                                    placeholder='Enter time in seconds'
                                />
                            </div>
                            <div className='form-group'>
                                <label htmlFor='T'>T</label>
                                <input
                                    type='text'
                                    {...register(`splits.${index}.T`)}
                                    className='form-control'
                                    disabled={isCompleted}
                                />
                            </div>
                        </div>
                    ))}
                    {!isCompleted && (
                        <div className={`${styles.addButton}`}>
                            <button
                                type='button'
                                onClick={onAddSplit}
                                className={`btn btn-primary ${styles.addButton}`}
                            >
                                Add
                            </button>
                        </div>
                    )}
                </div>
            </form>
        </Modal>
    );
}
